import { Injectable, inject } from '@angular/core';
import { AuthApiService } from '@api-new/authservice';
import { HTTP_CP_UpdateClientUser_Request, UserApiService } from '@api-new/userservice';
import { environment } from '@environments/environment';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action } from '@ngrx/store';
import { ErrorModel } from '@shared-lib/models/error.model';
import { ToastService, ToastSeverityEnum } from '@shared-lib/services/toast.service';
import { AnalyticsService } from '@shared/services/analytics.service';
import {
  agreeToTermsAndConditions,
  agreeToTermsAndConditionsFailure,
  agreeToTermsAndConditionsSuccess,
  deleteClientUser,
  deleteClientUserFailure,
  deleteClientUserSuccess,
  dismissClientUserLandlordPropertyBanner,
  dismissClientUserLandlordPropertyBannerFailure,
  dismissClientUserLandlordPropertyBannerSuccess,
  getClientUser,
  getClientUserFailure,
  getClientUserSuccess,
  provideClientUserDeleteCode,
  provideClientUserDeleteCodeFailure,
  provideClientUserDeleteCodeSuccess,
  updateClientUser,
  updateClientUserFailure,
  updateClientUserSilently,
  updateClientUserSuccess,
} from '@shared/store/client-user/client-user.actions';
import { loadInitialPlatformData } from '@shared/store/common.actions';
import dayjs from 'dayjs';
import { Observable } from 'rxjs';
import { of } from 'rxjs/internal/observable/of';
import { catchError, map, switchMap } from 'rxjs/operators';

const transformUserModelDatesToStrings = (user: HTTP_CP_UpdateClientUser_Request): HTTP_CP_UpdateClientUser_Request => ({
  ...user,
  dateOfBirth: user.dateOfBirth ? dayjs(user.dateOfBirth).format() : null,
});

@Injectable()
export class ClientUserEffects {
  private readonly userApiService = inject(UserApiService);
  private readonly authApiService = inject(AuthApiService);
  private readonly actions$ = inject(Actions);
  private readonly toastService = inject(ToastService);
  private readonly analyticsService = inject(AnalyticsService);

  handleLoadInitialPlatformData = createEffect(() => this.actions$.pipe(ofType(loadInitialPlatformData), map(getClientUser)));

  getUser = createEffect(() =>
    this.actions$.pipe(
      ofType(getClientUser),
      switchMap(() =>
        this.userApiService.HTTP_CP_GetClientUser().pipe(
          map((clientUser) => getClientUserSuccess({ clientUser })),
          catchError((error: ErrorModel) => of(getClientUserFailure({ error }))),
        ),
      ),
    ),
  );

  updateUser = createEffect(() =>
    this.actions$.pipe(
      ofType(updateClientUser),
      switchMap(({ clientUser }) => {
        return this.userApiService.HTTP_CP_UpdateClientUser(transformUserModelDatesToStrings(clientUser)).pipe(
          map((updatedUser) => {
            this.toastService.showToast(ToastSeverityEnum.Success, 'User was successfully updated');
            return updateClientUserSuccess({ clientUser: updatedUser });
          }),
          catchError((error) => {
            if (!environment.production) {
              console.error(error);
            }
            this.toastService.showToast(ToastSeverityEnum.Error, 'User failed to be updated');
            return of(updateClientUserFailure(error));
          }),
        );
      }),
    ),
  );

  silentUpdateUser = createEffect(() =>
    this.actions$.pipe(
      ofType(updateClientUserSilently),
      switchMap(({ clientUser }) => {
        return this.userApiService
          .HTTP_CP_UpdateClientUser(transformUserModelDatesToStrings(clientUser))
          .pipe(map((clientUser) => getClientUserSuccess({ clientUser })));
      }),
    ),
  );

  provideDeleteUserCode = createEffect(() =>
    this.actions$.pipe(
      ofType(provideClientUserDeleteCode),
      switchMap(() =>
        this.authApiService.HTTP_CP_ProvideClientUserDeleteCode().pipe(
          map(() => {
            this.toastService.showToast(ToastSeverityEnum.Success, 'Deletion e-mail has been sent to your e-mail address');
            return provideClientUserDeleteCodeSuccess();
          }),
          catchError((error) => {
            this.toastService.showToast(ToastSeverityEnum.Error, 'Unexpected error occurred while sending the deletion e-mail.');
            return of(provideClientUserDeleteCodeFailure(error));
          }),
        ),
      ),
    ),
  );

  deleteUserWithCode: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(deleteClientUser),
      switchMap(({ code }) =>
        this.authApiService.HTTP_CP_DeleteClientUser_WithCode({ code }).pipe(
          map(() => deleteClientUserSuccess()),
          catchError((error) => of(deleteClientUserFailure(error))),
        ),
      ),
    ),
  );

  dismissLandlordPropertyBanner = createEffect(() =>
    this.actions$.pipe(
      ofType(dismissClientUserLandlordPropertyBanner),
      switchMap(() =>
        this.userApiService.HTTP_CP_UpdateClientUser_DismissPropertyBanner().pipe(
          map((response) => {
            return dismissClientUserLandlordPropertyBannerSuccess({ clientUser: response });
          }),
          catchError((error: ErrorModel) => {
            this.toastService.showToast(ToastSeverityEnum.Error, 'The banner failed to be updated');
            return of(dismissClientUserLandlordPropertyBannerFailure({ error }));
          }),
        ),
      ),
    ),
  );

  loginSuccessActionWithAgreement = createEffect(() =>
    this.actions$.pipe(
      ofType(agreeToTermsAndConditions),
      switchMap(() =>
        this.userApiService.HTTP_CP_AcceptTermsAndConditions().pipe(
          map(() => agreeToTermsAndConditionsSuccess()),
          catchError((error) => of(agreeToTermsAndConditionsFailure({ error }))),
        ),
      ),
    ),
  );
}
